import { openKlaus, trackKlausImpression } from '@app/constants/klaus';
import { useMessages } from '@app/context/MessageContext';
import { Button, NotificationModal } from '@components/ui';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Launch announcement for Klaus, the EU-AI-Act sibling product from the team behind Dieter.
 *
 * The context carries the campaign slug: the dismissal is stored per context hash, so a later
 * campaign wave can reuse this component without inheriting the dismissals of this one.
 */
const CAMPAIGN = 'klaus-launch-2026-08';

export function KlausLaunchMessage() {
  const { t } = useTranslation();
  const { klauslaunch } = useMessages();

  useEffect(() => {
    klauslaunch.setContext({ name: 'klauslaunch', campaign: CAMPAIGN });
  }, []);

  useEffect(() => {
    if (klauslaunch.isOpen) trackKlausImpression('modal');
  }, [klauslaunch.isOpen]);

  const handleCta = () => {
    openKlaus('modal');
    klauslaunch.close();
  };

  return (
    <NotificationModal open={klauslaunch.isOpen} onClose={klauslaunch.close} closeButtonColor="white" unsolicited>
      <div className="flex flex-col p-[30px]">
        {/* Klaus blue, not Dieter's primary: the band belongs to the product being introduced */}
        <div className="bg-klaus-root -mx-[40px] px-9 py-7 -mt-[40px] flex flex-col items-center text-center gap-4">
          {/* Dieter introduces his sibling — both mascots waving side by side */}
          <div className="flex items-end justify-center gap-6">
            <img src="/assets/images/dieter-hello.png" alt="Dieter" className="h-[150px]" />
            <img src="/assets/images/klaus/klaus-waving.png" alt="Klaus" className="h-[150px]" />
          </div>
          <span className="text-white font-medium text-lg">
            <span className="block text-2xl font-bold">{t('klaus.modal.band_title')}</span>
            {t('klaus.modal.band_subtitle')}
          </span>
        </div>

        <img src="/assets/images/klaus/klaus-logo.svg" alt="Klaus kann KI" className="h-[46px] self-center mt-5" />

        <p className="mt-4">{t('klaus.modal.intro')}</p>
        <ul className="flex flex-col gap-2 mt-2">
          {['bullet1', 'bullet2', 'bullet3'].map((key) => (
            <li key={key} className="flex gap-3 items-start">
              <span>✅</span>
              <span>{t(`klaus.modal.${key}`)}</span>
            </li>
          ))}
        </ul>

        <div className="pointer-events-auto bg-white my-5">
          <div className="flex flex-col items-center text-center gap-3">
            <Button onClick={handleCta} data-testid="klaus-modal-cta">
              {t('klaus.modal.cta')}
            </Button>
            <button
              onClick={klauslaunch.close}
              className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
              data-testid="button-close"
            >
              {t('klaus.modal.later')}
            </button>
          </div>
        </div>
      </div>
    </NotificationModal>
  );
}
